import { isGuideSlug, isVerticalSlug } from 'content/marketingRoutes';
import type { Locale } from 'i18n/localePath';
import { withLocalePrefix } from 'i18n/localePath';
import en from 'i18n/messages/en';
import nl from 'i18n/messages/nl';

import absoluteUrl from './absoluteUrl';

const META = { en: en.meta, nl: nl.meta };
const MESSAGES = { en, nl };

function normalizePath(pathname: string): string {
  let p = pathname || '/';
  const q = p.indexOf('?');
  if (q !== -1) p = p.substring(0, q);
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function getRouteMeta(
  locale: Locale,
  path: string
): { title: string; description: string } | undefined {
  const m = META[locale];
  const messages = MESSAGES[locale];

  const map: Record<string, { title: string; description: string }> = {
    '/': m.home,
    '/onboard': m.onboard,
    '/terms': m.terms,
    '/privacy': m.privacy,
    '/cookies': m.cookies,
    '/contact': m.contact,
    '/compare': m.compare,
    '/checklist': m.checklist,
    '/pricing': m.pricing,
    '/for': m.forIndex,
    '/guides': m.guidesIndex,
  };

  if (map[path]) return map[path];

  if (path.startsWith('/for/')) {
    const slug = path.slice('/for/'.length);
    if (isVerticalSlug(slug)) {
      const vertical = messages.verticals[slug];
      return { title: vertical.metaTitle, description: vertical.metaDescription };
    }
  }

  if (path.startsWith('/guides/')) {
    const slug = path.slice('/guides/'.length);
    if (isGuideSlug(slug)) {
      const guide = messages.guides[slug];
      return { title: guide.metaTitle, description: guide.metaDescription };
    }
  }

  return undefined;
}

export type DocumentMetaProps = Record<string, unknown>;

export default function buildDocumentMetaProps({
  pathname,
  ogImageUrl,
  locale,
}: {
  pathname: string;
  ogImageUrl: string;
  locale: Locale;
}): DocumentMetaProps {
  const path = normalizePath(pathname);
  const canonicalPath = withLocalePrefix(locale, path);
  const canonical = absoluteUrl(canonicalPath === '/' ? '/' : canonicalPath);

  const route = getRouteMeta(locale, path);
  const notFound = META[locale].notFound;

  if (!route) {
    return {
      title: notFound.title,
      description: notFound.description,
      canonical,
      meta: {
        name: {
          robots: 'noindex, nofollow',
          description: notFound.description,
        },
      },
      auto: {},
    };
  }

  const { title, description } = route;
  const ogLocale = locale === 'nl' ? 'nl_NL' : 'en_NL';

  return {
    title,
    description,
    canonical,
    meta: {
      name: {
        description,
        keywords:
          'GastroBuddy, restaurant app, hospitality software, AI assistant, checklists, HACCP, cash register, recipes, mise en place, staff scheduling, Netherlands',
        'twitter:card': 'summary_large_image',
        'twitter:title': title,
        'twitter:description': description,
        ...(ogImageUrl ? { 'twitter:image': ogImageUrl } : {}),
      },
      property: {
        'og:type': 'website',
        'og:site_name': 'GastroBuddy',
        'og:title': title,
        'og:description': description,
        'og:url': canonical,
        'og:locale': ogLocale,
        ...(ogImageUrl
          ? {
              'og:image': ogImageUrl,
              'og:image:alt': 'GastroBuddy — hospitality operations for restaurants and bars',
            }
          : {}),
      },
    },
    auto: { ograph: true },
  };
}



// WEBPACK FOOTER //
// ./src/seo/pageMeta.ts